import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { usePostingSurfacePresence } from '@/features/notifications/hooks/usePostingSurfacePresence';
import { ArrowLeft, Loader2, RefreshCw, Send, Lock, Ban, Users, ClipboardList, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { cn } from '@/lib/utils';
import { usePicklistStore } from '../store';
import { picklistService } from '../services';
import { DocumentStatus, type PostingBreakdownRow } from '../types';
import { ManagePickersDialog } from '../components/ManagePickersDialog';
import { PicklistKpiStrip } from '../components/PicklistKpiStrip';
import { PicklistItemsTable } from '../components/PicklistItemsTable';
import { PostToMisDialog } from '../components/post-to-mis/PostToMisDialog';
import { SecondBatchConfirm } from '../components/post-to-mis/SecondBatchConfirm';
import { stillPosting } from '../components/post-to-mis/postRows';
import { PostingBatchesStrip } from '../components/PostingBatchesStrip';
import { PostedToNetSuiteCard } from '../components/PostedToNetSuiteCard';
import { PickHistoryCard } from '../components/PickHistoryCard';
import { usePicklistAutoRefresh } from '../hooks/usePicklistAutoRefresh';
import { PicklistBreadcrumb, PicklistHeaderActions } from '../components/PicklistHeaderBits';
import { StatusPill } from '../components/PicklistProgressBits';
import { aggregate, pickState, postState, postButtonState, documentStatusLabel, firstLinePerMrs, picklistRoles, qty, replaceAvailability } from '../utils/picklistProgress';
import { MrsChip, MrsHoverCardHost, useMrsHoverCardStore } from '../components/mrs-hover-card';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AdjustDialog } from '../components/adjust/AdjustDialog';
import { SwapSerialDialog } from '../components/swap-serial/SwapSerialDialog';
import { MRS_CHIP_SHAPE } from '@/components/MrsLink';
import { useAuthStore } from '@features/authentication/store/authStore';
import { ActivityDock, phtDateTime } from '@features/activity';
import { DetailPageLayout } from '@/components/layouts/DetailPageLayout';
import { PanelSkeleton } from '@/components/ui/skeleton';

// PW-13 — the page-level `statusConfig` map is GONE. The header now shows two quantity-weighted
// pills from `picklistProgress` (picking + posting), which is what a single PicklistStatus badge
// could never do: it had no way to say "fully picked, nothing posted".


export function PicklistDetailPage() {
  const { id } = useParams<{ id: string }>();
  // N8 — while this page is open and focused, its own posting outcome does not also toast: the
  // banner above has already said it. Fails toward SHOWING the toast (a null id registers nothing).
  usePostingSurfacePresence('posting.mis', id ?? null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get('from');
  const backUrl = fromTab === 'closed' ? '/mro/issuance/picklists?tab=closed' : '/mro/issuance/picklists';
  const {
    selectedPicklist,
    isLoadingDetail,
    detailError,
    fetchPicklistById,
    clearSelectedPicklist,
    retryPosting,
    isEditing,
    removeItems,
    isClosing,
    closePicklist,
    isCancelling,
    cancelPicklist,
  } = usePicklistStore();

  usePageTitle(selectedPicklist ? `Picklist ${selectedPicklist.picklistNumber}` : 'Picklist');

  // M4 (86d44z223) — the shell's Recent list. Keyed to the RESOLVED record, never to mount: at
  // mount `selectedPicklist` is null and this page keeps the previous one on screen while
  // refetching, so a mount-keyed capture would store an empty label or the wrong record.
  useRecordVisit('picklist', selectedPicklist?.id, selectedPicklist?.picklistNumber);

  // Current user role for this picklist
  const currentUserId = useAuthStore((s) => s.user?.userId);
  const currentUserHasNsEmployee = useAuthStore((s) => Boolean(s.user?.netSuiteEmployeeId));
  const hasManagePickersPermission = useAuthStore((s) => s.hasPermission(PERMISSIONS.ISSUANCE_MANAGE_PICKLIST_PICKERS));
  // manage_picklist_pickers is the full creator-equivalent admin path: it grants both
  // structural authority (cancel / edit / manage pickers) AND fulfillment authority
  // (pick / post / complete / adjust / close). view_all_picklists is intentionally NOT
  // a bypass anywhere — it grants visibility, not action authority.
  const { isCreator, isPicker } = picklistRoles(selectedPicklist, currentUserId, hasManagePickersPermission);

  // Remove confirmation state. An ITEM is removed, so the state is every line id of its group (PT-7 A7).
  const [removeItemIds, setRemoveItemIds] = useState<number[] | null>(null);
  const [removeItemCode, setRemoveItemCode] = useState('');

  // MIS Posting — PT-5: only whether the dialog is open lives here. Everything it edits (selection, quantities,
  // memos, the reading, serial ticks, the ClientKey) lives in the dialog's own store (post-to-mis/), so a
  // keystroke there never re-renders this page.
  const [showPostDialog, setShowPostDialog] = useState(false);

  // 86d357xee Phase 3 — collapsible per-unit rows in the items table.
  // 86d3n1xum — posting-allocation breakdown popover. Fetched once per picklist on first cell-open,
  // cached here, filtered per item. Replaces the old per-serial chevron sub-rows (which only showed the
  // PICKED state; the popover shows the actual ISSUED per-(bin,lot/serial) allocation from the ledger).
  const [breakdownRows, setBreakdownRows] = useState<PostingBreakdownRow[] | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);

  // The actual fetch (no guard) — used by the lazy opener below and by the cache-invalidation refetch.
  const fetchBreakdown = async () => {
    if (!selectedPicklist) return;
    setBreakdownLoading(true);
    setBreakdownError(null);
    try {
      const rows = await picklistService.getPostingBreakdown(selectedPicklist.id);
      setBreakdownRows(rows);
    } catch (err) {
      setBreakdownError(getApiErrorMessage(err, 'Failed to load posting breakdown'));
    } finally {
      setBreakdownLoading(false);
    }
  };

  // Lazy opener (onOpen): fetch once per cache-key. On error rows stay null so re-opening / Retry re-runs.
  const loadBreakdown = () => {
    if (breakdownLoading || breakdownRows !== null) return;
    void fetchBreakdown();
  };

  // 86d3n1xum — the breakdown is lazy-cached (fetched once per picklist, then reused across cell-opens).
  // When the picklist's posted total changes (a post just landed) or we switch picklists, the snapshot is
  // stale: if it was already loaded (a popover may be open), refetch in place so it updates instead of
  // blanking to empty; otherwise just clear so the first cell-open fetches. Without this the popover served
  // the pre-post snapshot until a manual reload — and an open popover blanked on a background post-settle.
  const breakdownCacheKey = selectedPicklist
    ? `${selectedPicklist.id}:${selectedPicklist.items.reduce((sum, i) => sum + i.quantityPosted, 0)}`
    : null;
  useEffect(() => {
    if (breakdownRows !== null) void fetchBreakdown();
    else setBreakdownError(null);
    // Keyed only on the cache key; intentionally uses the latest breakdownRows/fetchBreakdown closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownCacheKey]);

  const [showSecondBatchConfirm, setShowSecondBatchConfirm] = useState(false);

  // Close confirmation state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // PT-6 scope 14 — Adjust's target is an item id: the group's anchor (the Items row). The dialog
  // resolves it to the whole item and snapshots it; everything it edits lives in the dialog, never here.
  const [adjustTargetItemId, setAdjustTargetItemId] = useState<number | null>(null);
  // PT-6 scope 15/16 — the bin card's Swap serial: the item group and its taken serial display bin, by id.
  const [swapTarget, setSwapTarget] = useState<{ groupId: number; binId: number } | null>(null);

  const hasAdjustPermission = useAuthStore((s) => s.hasPermission(PERMISSIONS.ISSUANCE_ADJUST_PICKLIST));

  // ISS-R-134 — Adjust and Swap serial need the permission AND a picker's standing on this picklist, as the
  // server requires. Until 2026-10-05 the page asked for the permission alone, so a non-picker saw an enabled
  // control and was refused. The per-item reasons (nothing picked, posted, held) are `adjustAvailability`'s.
  const canAdjustHere = hasAdjustPermission && isPicker;
  const canAdjustOnPicklist = selectedPicklist != null && canAdjustHere &&
    selectedPicklist.documentStatus !== DocumentStatus.Cancelled;

  // Manage pickers dialog state
  const [showPickersDialog, setShowPickersDialog] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPicklistById(Number(id));
    }

    // Cleanup on unmount
    return () => {
      clearSelectedPicklist();
    };
  }, [id, fetchPicklistById, clearSelectedPicklist]);

  // Owner 2026-10-08 — kept current for changes made elsewhere: every 30 seconds and on coming back to the tab, through
  // the store's one refresh path. Starts once the picklist is on screen.
  usePicklistAutoRefresh(selectedPicklist?.id ?? null);

  // PT-7 B3 — the by-id read carries this picklist's MRS summaries; the page's ONE hover card reads them from
  // its store. Seeded from an effect, never in render, and again on every refetch. An empty array (the raw
  // reads that answer Add/Remove Picker and Remove Items) never overwrites a loaded entry — the store's rule.
  const summaryPicklistId = selectedPicklist?.id;
  const mrsSummaries = selectedPicklist?.mrsSummaries;
  useEffect(() => {
    if (summaryPicklistId != null) {
      useMrsHoverCardStore.getState().seedSummaries(summaryPicklistId, mrsSummaries);
    }
  }, [summaryPicklistId, mrsSummaries]);

  // --- Editing handlers ---
  // Only the creator (or manage_picklist_pickers holder, treated as creator-equivalent above)
  // can edit items and cancel; pickers can pick, post MIS, and close.
  const hasEditPermission = useAuthStore((s) => s.hasPermission(PERMISSIONS.ISSUANCE_EDIT_PICKLIST));
  const canEdit = selectedPicklist != null && isCreator && hasEditPermission &&
    (selectedPicklist.documentStatus === DocumentStatus.Draft || selectedPicklist.documentStatus === DocumentStatus.Open) &&
    // ISS-R-023 — open for line edits while nothing is picked right now (it used to lock for good after any pick).
    selectedPicklist.items.every((item) => item.quantityPicked === 0);

  const handleRemoveItem = (picklistItemIds: number[], itemCode: string) => {
    setRemoveItemIds(picklistItemIds);
    setRemoveItemCode(itemCode);
  };

  const handleConfirmRemove = async () => {
    if (removeItemIds == null || removeItemIds.length === 0) return;

    try {
      await removeItems(removeItemIds);
      toast.success(`Removed item ${removeItemCode} from picklist`);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to remove item');
      toast.error('Failed to remove item', { description: message });
    } finally {
      setRemoveItemIds(null);
      setRemoveItemCode('');
    }
  };

  // --- Close handlers ---
  // Can close if: Open, at least one item posted, and no unposted picked quantities
  const canClose = selectedPicklist != null && isPicker &&
    selectedPicklist.documentStatus === DocumentStatus.Open &&
    selectedPicklist.items.some((item) => item.quantityPosted > 0) &&
    !selectedPicklist.items.some((item) => item.quantityPicked > item.quantityPosted);

  // Check if any items have unpicked quantities (partial fulfillment)
  const hasUnpickedQuantities = selectedPicklist != null &&
    selectedPicklist.items.some((item) => item.quantityPicked < item.quantityToPick);

  const handleClose = async () => {
    if (hasUnpickedQuantities && !closeReason.trim()) return;
    const result = await closePicklist(hasUnpickedQuantities ? closeReason.trim() : undefined);
    if (result !== true) {
      // The server's own reason; the dialog stays open with the reason the user typed.
      toast.error(result);
      return;
    }
    toast.success('Picklist closed successfully');
    setShowCloseDialog(false);
    setCloseReason('');
  };

  // --- Cancel handlers ---
  // Can cancel if: Draft or Open, nothing posted, and no unvoided picks
  const canCancel = selectedPicklist != null && isCreator &&
    (selectedPicklist.documentStatus === DocumentStatus.Draft || selectedPicklist.documentStatus === DocumentStatus.Open) &&
    !selectedPicklist.items.some((item) => item.quantityPosted > 0) &&
    !selectedPicklist.items.some((item) => !item.isVoided && item.quantityPicked > 0);

  const handleCancel = async () => {
    if (!cancelReason.trim()) return;
    const result = await cancelPicklist(cancelReason.trim());
    if (result !== true) {
      toast.error(result);
      return;
    }
    toast.success('Picklist cancelled successfully');
    setShowCancelDialog(false);
    setCancelReason('');
  };

  // --- MIS Posting handlers ---
  // Items with unposted picked quantity — remain eligible for partial re-posting until fully posted
  const eligibleItems = useMemo(
    () => selectedPicklist?.items.filter((item) => !item.isVoided && item.quantityPicked - item.quantityPosted > 0) ?? [],
    [selectedPicklist?.items]
  );

  // N8 review (2026-08-25): MIS was the ONLY posting surface with no busy state — an `await` sat
  // between the Retry click and the first visible change, the named red flag of the responsiveness
  // gate. Keyed by BATCH so one in-flight batch does not disable its siblings' buttons.
  const [resolvingBatchId, setResolvingBatchId] = useState<number | null>(null);

  // 86d3kwt7h — retry a failed MIS post attempt (from a Failed posting card).
  const handleRetryPosting = async (postingBatchId: number) => {
    setResolvingBatchId(postingBatchId);
    try {
      const result = await retryPosting(postingBatchId);
      if (typeof result === 'number') {
        toast.success('Retrying', { description: 'Re-posting the MIS to NetSuite…', duration: 5000 });
      } else {
        toast.error('Retry failed', { description: result.error, duration: 8000 });
      }
    } finally {
      setResolvingBatchId(null);
    }
  };

  // 86d3mnuxe funnel — cancel a NotReached-failed batch posting (Failed → Cancelled; the items'
  // unposted quantities were never decremented, so a fresh batch posts them normally). The BE
  // refuses (400) when the post may have reached NetSuite — its message says to Retry instead.
  const handleCancelPosting = async (postingBatchId: number) => {
    if (!id) return;
    setResolvingBatchId(postingBatchId);
    try {
      await picklistService.cancelMisPost(postingBatchId);
      toast.success('Posting cancelled', { description: 'The failed post is closed.', duration: 5000 });
      await fetchPicklistById(Number(id));
    } catch (err) {
      toast.error('Cancel failed', {
        description: err instanceof Error ? err.message : 'Unable to cancel the posting',
        duration: 8000,
      });
    } finally {
      setResolvingBatchId(null);
    }
  };

  // ⚠️ Skeleton ONLY when there is nothing to show yet. Every posting mutation refetches the detail
  // (see the awaits on fetchPicklistById below), and gating on `isLoadingDetail` alone unmounted the
  // ENTIRE page — including the ActivityDock rail — on every post/retry/cancel. The remounted dock
  // starts from INITIAL_FEED_STATE, so the user watched the activity feed blank to a skeleton on each
  // mutation even though the dock's own reducer does stale-while-revalidate (2026-08-25, owner RP e2e).
  // Navigating between picklists still skeletons: the id-change effect cleanup clears selectedPicklist
  // to null before the next fetch starts.
  // PW-13 — all three async states render INSIDE the shell, with the breadcrumb already in the header
  // slot. Returning early used to mount a full-width page of skeletons and then swap it for a
  // two-column page with a rail, so arriving at a picklist made the whole layout jump.
  const shellBreadcrumb = (
    <PicklistBreadcrumb backUrl={backUrl} picklistNumber={selectedPicklist?.picklistNumber} />
  );

  if (isLoadingDetail && !selectedPicklist) {
    return (
      <DetailPageLayout
        selfSizedRail
        header={shellBreadcrumb}
        rail={<ActivityDock entityType="Picklist" entityId={Number(id)} />}
      >
        <div className="space-y-4 p-6">
          <PanelSkeleton rows={4} />
          <PanelSkeleton rows={6} />
          <PanelSkeleton rows={4} />
        </div>
      </DetailPageLayout>
    );
  }

  if (detailError || !selectedPicklist) {
    return (
      <DetailPageLayout
        selfSizedRail
        header={shellBreadcrumb}
        rail={<ActivityDock entityType="Picklist" entityId={Number(id)} />}
      >
        <div className="p-6">
          <EmptyState
            icon={ClipboardList}
            title={detailError ? "Couldn't load this picklist" : 'Picklist not found'}
            description={detailError ?? 'It may have been removed, or you may not have access to it.'}
            action={
              detailError ? (
                <Button variant="outline" onClick={() => id && fetchPicklistById(Number(id))}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate(backUrl)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Picklists
                </Button>
              )
            }
          />
        </div>
      </DetailPageLayout>
    );
  }

  const {
    picklistNumber,
    notes,
    createdAt,
    mrsNumbers,
  } = selectedPicklist;
  // PT-7 A8 — each header chip stands for an MRS number and takes its id, requestor and department from the
  // FIRST planned line with that number, groups by anchor then planned-line order. From the item view, never
  // `items[]` (an MRS line is not an item).
  const mrsChipLine = new Map(
    firstLinePerMrs((selectedPicklist.itemGroups ?? []).flatMap((g) => g.plannedLines ?? [])).map(
      (line) => [line.mrsNumber, line] as const,
    ),
  );
  const swapGroup = swapTarget ? (selectedPicklist.itemGroups ?? []).find((g) => g.id === swapTarget.groupId) : undefined;
  const swapBin = swapGroup?.bins.find((b) => b.id === swapTarget!.binId);

  const agg = aggregate(selectedPicklist);
  const canManagePickers = selectedPicklist.documentStatus !== DocumentStatus.Closed
    && selectedPicklist.documentStatus !== DocumentStatus.Cancelled
    && isCreator;
  // ⚠️ The BUTTON's visibility still keys off eligibleItems (a line COUNT) — that is the existing
  // hidden-state rule and it is unchanged. What the button SAYS and whether it can be pressed now come
  // from the quantity the server would actually accept.
  const canPost = isPicker && eligibleItems.length > 0 && !selectedPicklist.isPosted;
  const postButton = postButtonState(selectedPicklist, agg);

  // PT-5 deliverable 10 — while a batch is still POSTING, the second-batch confirm first; otherwise straight in.
  const handlePostPressed = () => {
    if (stillPosting(selectedPicklist).length > 0) setShowSecondBatchConfirm(true);
    else setShowPostDialog(true);
  };

  const header = (
    <div className="space-y-3 pb-3">
      <PicklistBreadcrumb backUrl={backUrl} picklistNumber={picklistNumber} />

      <div className="pl-header-row flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {/* Owner, 2026-10-05 — who created it and when is off the meta line and on this hover. The activity
                log is the usual place to look, but 685 picklists from before the log (2026-03-11 to 05-21) have
                no creation row there, so the fact stays reachable from the page itself. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <h1
                  tabIndex={0}
                  className="cursor-help rounded font-mono text-2xl font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {picklistNumber}
                </h1>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Created {phtDateTime(createdAt)}
                {selectedPicklist.createdByName ? ` by ${selectedPicklist.createdByName}` : ''}
              </TooltipContent>
            </Tooltip>
            {/* The DOCUMENT badge — a different axis from the two pills beside it, and neutral on
                purpose. This page used to style `Closed` as DESTRUCTIVE, which read as a failure state
                for the ordinary end of a picklist's life. Draft is drawn too: it is reachable, and it
                cannot be closed, so a picklist sitting in it with no label is a dead end nobody can see. */}
            <Badge variant="outline" className="font-normal">
              {documentStatusLabel(selectedPicklist.documentStatus)}
            </Badge>
            {/* Two pills, both quantity-weighted, same vocabulary as the landing — an operator moving
                from the list to this page has to recognise the picklist they clicked. */}
            <StatusPill state={pickState(selectedPicklist, agg)} />
            <StatusPill state={postState(selectedPicklist, agg)} />
          </div>

          {/* Meta line. The MRS numbers live HERE now, as links — the KPI card that used to hold the
              only picklist→MRS deep link is replaced by the gap card in this same commit (D36).
              Every MRS number is drawn, however many (owner, 2026-10-05): this line is the picklist's index
              of what it is for, so it does not collapse to a count the way a table cell does. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            {mrsNumbers.length > 0 && (
              <span className="flex flex-wrap items-center gap-1" data-mrs-chip-group="">
                <span>MRS</span>
                {/* The CHIP, same as the one the items table draws (owner ruling 2026-09-22). One MRS
                    number should not wear two different costumes on one page — the underlined-link
                    variant here made the header links read as a different kind of thing from the chips
                    six rows below, when they navigate to exactly the same place.
                    The unlinked fallback wears the chip's shape too, so a missing id changes the colour
                    and the cursor, not the layout. */}
                {mrsNumbers.map((mrs) => {
                  const line = mrsChipLine.get(mrs);
                  return line !== undefined ? (
                    <MrsChip
                      key={mrs}
                      picklistId={selectedPicklist.id}
                      picklistCreatedAt={createdAt}
                      materialRequestId={line.materialRequestId}
                      mrsNumber={mrs}
                      requestorFullName={line.requestorFullName}
                      departmentName={line.departmentName}
                    />
                  ) : (
                    <span key={mrs} className={cn(MRS_CHIP_SHAPE, 'text-muted-foreground')}>
                      {mrs}
                    </span>
                  );
                })}
              </span>
            )}
            {selectedPicklist.pickers.length > 0 && (
              <span className="flex flex-wrap items-center gap-1">
                {mrsNumbers.length > 0 && <span aria-hidden>·</span>}
                <Users className="h-3.5 w-3.5" aria-hidden />
                <span>
                  {selectedPicklist.pickers.length === 1 ? 'Picker' : 'Pickers'}:{' '}
                  {selectedPicklist.pickers.map((pk) => pk.fullName).join(', ')}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Action row. Manage pickers · Close picklist · Post stay inline; Add Items · Cancel live in the
            overflow menu, because a wrapping title plus the activity rail leaves room for about three
            controls at 1280px. Close was promoted out of the menu and Print deleted outright (owner,
            2026-09-14): closing is the only terminal state a part-picked picklist can reach, so it should
            not be two clicks deep. Every control keeps the guard it ships with — a user without the
            permission sees NO control rather than a disabled one — and the ellipsis itself does not render
            when both its items are guarded away, so it never opens an empty menu. */}
        <div className="flex shrink-0 items-center gap-2">
          {agg.inflight > 0 && (
            // "Posting", never "committed" — the word read as accounting jargon, and as *promised*
            // rather than *on its way*, which is the one thing about these units that matters.
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="inline-flex items-center gap-1.5 rounded-full bg-info/10 px-2.5 py-1 text-xs font-medium text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden />
                  {qty(agg.inflight)} posting
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {qty(agg.inflight)} picked units are in an open posting batch and excluded from the next one,
                so pressing Post again cannot issue them twice.
              </TooltipContent>
            </Tooltip>
          )}
          {canManagePickers && (
            <Button variant="outline" size="sm" onClick={() => setShowPickersDialog(true)}>
              <Users className="mr-2 h-4 w-4" />
              Manage pickers
            </Button>
          )}
          {canClose && (
            <PermissionGuard permission={PERMISSIONS.ISSUANCE_CLOSE_PICKLIST}>
              <Button variant="outline" size="sm" onClick={() => setShowCloseDialog(true)}>
                <Lock className="mr-2 h-4 w-4" />
                Close picklist
              </Button>
            </PermissionGuard>
          )}
          {canPost && (
            <PermissionGuard permission={PERMISSIONS.ISSUANCE_POST_PICKLIST}>
              {/* The NetSuite-employee precondition was a bare `title` on a span, which is invisible to
                  keyboard users and to screen readers on a DISABLED button. Tooltip + aria-label. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* The span is focusable only when the button is NOT — a disabled button takes no
                      focus, so without it the explanation is unreachable by keyboard. */}
                  <span tabIndex={postButton.disabled || !currentUserHasNsEmployee ? 0 : -1}>
                    <Button
                      size="sm"
                      onClick={handlePostPressed}
                      disabled={!currentUserHasNsEmployee || postButton.disabled}
                      aria-label={
                        currentUserHasNsEmployee
                          ? postButton.label
                          : 'Post to MIS — unavailable, your account is not linked to a NetSuite employee'
                      }
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {postButton.label}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {currentUserHasNsEmployee
                    ? postButton.tooltip
                    : 'Your account is not linked to a NetSuite employee. Contact an administrator to enable posting.'}
                </TooltipContent>
              </Tooltip>
            </PermissionGuard>
          )}
          <PicklistHeaderActions
            canEdit={canEdit}
            canCancel={canCancel}
            onAddItems={() => navigate(`/mro/issuance/picklist/${id}/add-items`)}
            onCancel={() => setShowCancelDialog(true)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <DetailPageLayout
      selfSizedRail
      header={header}
      rail={<ActivityDock entityType="Picklist" entityId={selectedPicklist.id} />}
    >
      <div className="space-y-4 p-6">

      <PostingBatchesStrip
        picklist={selectedPicklist}
        resolvingBatchId={resolvingBatchId}
        onRetry={handleRetryPosting}
        onCancel={(batchId) => { void handleCancelPosting(batchId); }}
      />

      {/* Closing reason banner */}
      {selectedPicklist.documentStatus === DocumentStatus.Closed && selectedPicklist.remarks && (
        <div className="rounded-md border border-warning/30 bg-warning/10 p-4">
          <p className="text-sm font-medium text-warning">Partial Close Reason</p>
          <p className="text-sm text-muted-foreground mt-1">{selectedPicklist.remarks}</p>
        </div>
      )}

      {/* Cancellation reason banner */}
      {selectedPicklist.documentStatus === DocumentStatus.Cancelled && selectedPicklist.remarks && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4">
          <p className="text-sm font-medium text-destructive">Cancellation Reason</p>
          <p className="text-sm text-muted-foreground mt-1">{selectedPicklist.remarks}</p>
        </div>
      )}

      <PicklistKpiStrip picklist={selectedPicklist} />

      {/* PW-26 — between the KPI strip and the Items table, per the design. */}
      <PostedToNetSuiteCard
        picklist={selectedPicklist}
        rows={breakdownRows}
        loading={breakdownLoading}
        error={breakdownError}
        onLoad={loadBreakdown}
      />

      <PicklistItemsTable
        picklist={selectedPicklist}
        canAdjust={canAdjustOnPicklist}
        canEdit={canEdit}
        isEditing={isEditing}
        onAdjust={(group) => setAdjustTargetItemId(group.id)}
        onReplaceSerial={canAdjustHere ? (group, bin) => setSwapTarget({ groupId: group.id, binId: bin.id }) : undefined}
        replaceAvailability={replaceAvailability(selectedPicklist, canAdjustHere)}
        onRemove={handleRemoveItem}
        breakdownRows={breakdownRows}
        breakdownLoading={breakdownLoading}
        breakdownError={breakdownError}
        onLoadBreakdown={loadBreakdown}
      />

      {/* Owner 2026-10-08 — the Pick history is back, picklist-wide and read from the pick ledger (see the PW-19 note
          below). Keyed by picklist so its search, filter and opened groups reset when the picklist changes. */}
      <PickHistoryCard key={selectedPicklist.id} picklist={selectedPicklist} />

      {/* Notes — LAST in the records column, per the prototype (items card at :323, notes at :392).
          It sat between the KPI strip and the items table, so free text pushed the working surface
          down the page. It is a closing footnote to the record, not a header. */}
      {notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <StickyNote className="h-4 w-4 text-muted-foreground" aria-hidden />
              Notes
              {/* Accurate, and worth saying: Notes is written once at creation and there is no
                  notes-edit endpoint anywhere, so read-only is the design rather than a limitation. */}
              <span className="text-xs font-normal text-muted-foreground">
                · free text from whoever created this picklist
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="max-w-xl text-sm text-muted-foreground">{notes}</p>
          </CardContent>
        </Card>
      )}

      {/* PW-19 — the per-item Picking History card lived here and was RETIRED for the activity rail. Owner 2026-10-08:
          the rail cannot be searched, so serial-heavy picklists were unreadable there; PickHistoryCard (under Items)
          brings the history back, picklist-wide and read from the pick ledger, and the rail's pick entries now show
          only item and units.

          ⚠️ `PicklistItemDto.pickingHistories` STAYS ON THE WIRE. It is not decoration for this card: the
          Post dialog pre-fills each line's meter Reading from the last history row that carries one. Drop
          the field and that pre-fill silently yields nothing, which is a WRONG READING on a real MIS —
          the one failure here that is invisible from the screen. */}

      {/* Remove Item Confirmation Dialog */}
      <Dialog open={removeItemIds !== null} onOpenChange={(open) => { if (!open) { setRemoveItemIds(null); setRemoveItemCode(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <span className="font-mono font-medium">{removeItemCode}</span> from this picklist? This will restore its available quantity for future picklists.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRemoveItemIds(null); setRemoveItemCode(''); }} disabled={isEditing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemove} disabled={isEditing}>
              {isEditing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PT-5 — Post to MIS v2 (post-to-mis/). No final confirm (owner 2026-09-30): the dialog's own Post button
          runs the claim. */}
      <PostToMisDialog
        open={showPostDialog}
        onOpenChange={setShowPostDialog}
        picklist={selectedPicklist}
      />

      {/* Close Picklist Confirmation Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={(open) => {
        setShowCloseDialog(open);
        if (!open) setCloseReason('');
      }}>
        <DialogContent className={hasUnpickedQuantities ? "max-w-md" : "max-w-sm"}>
          <DialogHeader>
            <DialogTitle>Close Picklist</DialogTitle>
            <DialogDescription>
              Are you sure you want to close <span className="font-mono font-medium">{picklistNumber}</span>? Once closed, no further picking, posting, or editing will be allowed.
            </DialogDescription>
          </DialogHeader>
          {hasUnpickedQuantities && (
            <div className="space-y-2">
              <div className="rounded-md border border-warning/30 bg-warning/10 p-3">
                <p className="text-sm text-warning">
                  This picklist has items with unpicked quantities. Please provide a reason for closing with partial fulfillment.
                </p>
              </div>
              <Label htmlFor="close-reason">Reason for partial close <span className="text-destructive">*</span></Label>
              <Textarea
                id="close-reason"
                placeholder="e.g., Item out of stock, item damaged, substitution arranged..."
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">{closeReason.length}/500</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCloseDialog(false); setCloseReason(''); }} disabled={isClosing}>
              Cancel
            </Button>
            <Button onClick={handleClose} disabled={isClosing || (hasUnpickedQuantities && !closeReason.trim())}>
              {isClosing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Closing...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Close Picklist
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Picklist Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={(open) => {
        setShowCancelDialog(open);
        if (!open) setCancelReason('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Picklist</DialogTitle>
            <DialogDescription>
              This will void <span className="font-mono font-medium">{picklistNumber}</span> and release all items back to the available pool. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason for cancellation <span className="text-destructive">*</span></Label>
            <Textarea
              id="cancel-reason"
              placeholder="Enter reason for cancellation..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">{cancelReason.length}/500</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelDialog(false); setCancelReason(''); }} disabled={isCancelling}>
              Back
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={isCancelling || !cancelReason.trim()}>
              {isCancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Cancel Picklist
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PT-6 — Adjust per bin × MRS line (replaces AdjustItemDialog), on the whole item the target id names. */}
      <AdjustDialog targetId={adjustTargetItemId} onClose={() => setAdjustTargetItemId(null)} />

      {/* PT-6 scope 16 — the bin card's Swap serial, opened only from a taken serial's card. */}
      <SwapSerialDialog
        target={swapGroup && swapBin ? { group: swapGroup, bin: swapBin } : null}
        picklistId={selectedPicklist.id}
        onClose={() => setSwapTarget(null)}
      />

      {/* PT-7 B1 — the page's ONE MRS hover card, shared by the header chips and every Items-table chip. It
          portals into the content column; closed, nothing of it is in the DOM. */}
      <MrsHoverCardHost />

      {/* PT-5 deliverable 10 — the second-batch confirmation, a viewport dialog. */}
      <SecondBatchConfirm
        open={showSecondBatchConfirm}
        onOpenChange={setShowSecondBatchConfirm}
        picklist={selectedPicklist}
        postable={agg.postable}
        onGo={() => { setShowSecondBatchConfirm(false); setShowPostDialog(true); }}
      />

      {/* Manage Pickers Dialog */}
      <ManagePickersDialog
        open={showPickersDialog}
        onOpenChange={setShowPickersDialog}
      />
      </div>
    </DetailPageLayout>
  );
}
